import React, { useState, useEffect } from 'react';
import { 
  Car, 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Calendar, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  User,
  UserPlus,
  UserX
} from 'lucide-react';
import { deleteVehicle, subscribeToVehicles } from '../services/vehicleService';
import { getDrivers } from '../services/driverService';
import { 
  subscribeToAssignments,
  createAssignment,
  removeVehicleAssignment
} from '../services/assignmentService';

const VehiclesList = ({ onAddVehicle, onEditVehicle }) => {
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  useEffect(() => {
    // Subscribe to real-time updates
    const vehiclesUnsubscribe = subscribeToVehicles((vehiclesData) => {
      setVehicles(vehiclesData);
      setLoading(false);
    });

    // Subscribe to assignments
    const assignmentsUnsubscribe = subscribeToAssignments((assignmentsData) => {
      setAssignments(assignmentsData);
    });

    // Load drivers
    loadDrivers();

    return () => {
      vehiclesUnsubscribe();
      assignmentsUnsubscribe();
    };
  }, []);

  const loadDrivers = async () => {
    try {
      const driversData = await getDrivers();
      setDrivers(driversData);
    } catch (error) {
      console.error('Error loading drivers:', error);
    }
  };

  const getVehicleAssignmentData = (vehicleId) => {
    return assignments.find(a => a.vehicle_id === vehicleId && a.status === 'active');
  };

  const getAssignedDriver = (vehicleId) => {
    const assignment = getVehicleAssignmentData(vehicleId);
    if (!assignment) return null;
    return drivers.find(d => d.id === assignment.driver_id);
  };

  const getAvailableDrivers = () => {
    return drivers;
  };

  const handleAssignDriver = async (vehicleId, driverId, startDate, endDate) => {
    try {
      // First, remove any existing assignment for this vehicle
      await removeVehicleAssignment(vehicleId);

      // Create new assignment
      const newAssignment = await createAssignment({
        vehicle_id: vehicleId,
        driver_id: driverId,
        start_date: startDate,
        end_date: endDate
      });

      console.log('Driver assigned successfully:', newAssignment);
      setShowAssignmentModal(false);
      setSelectedVehicle(null);
    } catch (error) {
      console.error('Error assigning driver:', error);
      alert('Error assigning driver: ' + error.message);
    }
  };

  const handleRemoveAssignment = async (vehicleId) => {
    const assignedDriver = getAssignedDriver(vehicleId);
    if (!assignedDriver) return;

    if (window.confirm(`Are you sure you want to remove ${assignedDriver.name} from this vehicle?`)) {
      try {
        await removeVehicleAssignment(vehicleId);
        console.log('Assignment removed successfully');
      } catch (error) {
        console.error('Error removing assignment:', error);
        alert('Error removing assignment: ' + error.message);
      }
    }
  };

  useEffect(() => {
    // Filter vehicles based on search and status
    let filtered = vehicles;

    if (searchTerm) {
      filtered = filtered.filter(vehicle =>
        vehicle.registration_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vehicle.make?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(vehicle => vehicle.status === statusFilter);
    }

    setFilteredVehicles(filtered);
  }, [vehicles, searchTerm, statusFilter]);

  const handleDeleteVehicle = async (vehicleId, registrationNo) => {
    if (window.confirm(`Are you sure you want to delete vehicle ${registrationNo}?`)) {
      try {
        setDeleteLoading(vehicleId);
        await deleteVehicle(vehicleId);
        // Remove assignments handled by database cascade or you can add:
        await removeVehicleAssignment(vehicleId);
      } catch (error) {
        alert('Error deleting vehicle: ' + error.message);
      } finally {
        setDeleteLoading(null);
      }
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Active':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'Inactive':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    switch (status) {
      case 'Active':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'Inactive':
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  // Assignment Modal Component
  const AssignmentModal = ({ vehicle, onClose }) => {
    const [selectedDriverId, setSelectedDriverId] = useState('');
    const [startDate, setStartDate] = useState(
      getVehicleAssignmentData(vehicle?.id)?.start_date || 
      new Date().toISOString().split('T')[0]
    );
    const [endDate, setEndDate] = useState(
      getVehicleAssignmentData(vehicle?.id)?.end_date || 
      new Date(Date.now() + 365*24*60*60*1000).toISOString().split('T')[0]
    );

    const availableDrivers = getAvailableDrivers();
    const assignedDriver = getAssignedDriver(vehicle?.id);

    useEffect(() => {
      if (assignedDriver) {
        setSelectedDriverId(assignedDriver.id);
      }
    }, [assignedDriver]);

    const handleSubmit = (e) => {
      e.preventDefault();
      if (!selectedDriverId) {
        alert('Please select a driver');
        return;
      }
      if (new Date(endDate) < new Date(startDate)) {
        alert('End date cannot be before start date');
        return;
      }
      handleAssignDriver(vehicle.id, selectedDriverId, startDate, endDate);
    };

    if (!vehicle) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {assignedDriver ? 'Reassign' : 'Assign'} Driver
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>

          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="font-medium text-gray-900">
              {vehicle.registration_no}
            </div>
            <div className="text-sm text-gray-600">{vehicle.make}</div>
            {assignedDriver && (
              <div className="text-sm text-blue-600 mt-1">
                Currently assigned to: {assignedDriver.name}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Driver
              </label>
              <select
                value={selectedDriverId}
                onChange={(e) => setSelectedDriverId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Choose a driver...</option>
                {availableDrivers.map(driver => (
                  <option key={driver.id} value={driver.id}>
                    {driver.name} ({driver.phone})
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                type="submit"
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
              >
                {assignedDriver ? 'Reassign Driver' : 'Assign Driver'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading vehicles...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-2">
          <Car className="w-6 h-6 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Vehicles</h1>
          <span className="px-2 py-1 text-sm bg-blue-100 text-blue-800 rounded-full">
            {filteredVehicles.length}
          </span>
        </div>
        <button
          onClick={onAddVehicle}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <Plus className="w-4 h-4" />
          Add Vehicle
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by registration or make..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>

      {/* Vehicles Grid */}
      {filteredVehicles.length === 0 ? (
        <div className="text-center py-12">
          <Car className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No vehicles found</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm || statusFilter !== 'all' 
              ? 'No vehicles match your search criteria.' 
              : 'Get started by adding your first vehicle.'}
          </p>
          {!searchTerm && statusFilter === 'all' && (
            <button
              onClick={onAddVehicle}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              Add First Vehicle
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredVehicles.map((vehicle) => {
            const assignedDriver = getAssignedDriver(vehicle.id);
            
            return (
              <div
                key={vehicle.id}
                className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow"
              >
                {/* Vehicle Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Car className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-gray-900">
                        {vehicle.registration_no}
                      </h3>
                      <p className="text-sm text-gray-600">{vehicle.make}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {getStatusIcon(vehicle.status)}
                    <span className={getStatusBadge(vehicle.status)}>
                      {vehicle.status}
                    </span>
                  </div>
                </div>

                {/* Driver Assignment Info */}
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-700">Driver:</span>
                    </div>
                    {assignedDriver ? (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-900">{assignedDriver.name}</span>
                        <button
                          onClick={() => handleRemoveAssignment(vehicle.id)}
                          className="text-red-500 hover:text-red-700"
                          title="Remove assignment"
                        >
                          <UserX className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500 italic">No driver assigned</span>
                    )}
                  </div>
                </div>

                {/* Vehicle Details */}
                <div className="space-y-3 mb-4">
                  {vehicle.year && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>Year: {vehicle.year}</span>
                    </div>
                  )}

                  {/* {vehicle.last_service_mileage && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Gauge className="w-4 h-4" />
                      <span>Last Service: {vehicle.last_service_mileage.toLocaleString()} km</span>
                    </div>
                  )} */}

                  {/* <div className="flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4" />
                    <span className={`${
                      isExpired(vehicle.control_technique_date) 
                        ? 'text-red-600' 
                        : isExpiringSoon(vehicle.control_technique_date) 
                        ? 'text-yellow-600' 
                        : 'text-gray-600'
                    }`}>
                      Inspection: {formatDate(vehicle.control_technique_date)}
                      {isExpired(vehicle.control_technique_date) && (
                        <AlertTriangle className="w-4 h-4 inline ml-1 text-red-500" />
                      )}
                      {!isExpired(vehicle.control_technique_date) && isExpiringSoon(vehicle.control_technique_date) && (
                        <AlertTriangle className="w-4 h-4 inline ml-1 text-yellow-500" />
                      )}
                    </span>
                  </div> */}

                  {/* <div className="flex items-center gap-2 text-sm">
                    <Shield className="w-4 h-4" />
                    <span className={`${
                      isExpired(vehicle.insurance_expiry_date) 
                        ? 'text-red-600' 
                        : isExpiringSoon(vehicle.insurance_expiry_date) 
                        ? 'text-yellow-600' 
                        : 'text-gray-600'
                    }`}>
                      Insurance: {formatDate(vehicle.insurance_expiry_date)}
                      {isExpired(vehicle.insurance_expiry_date) && (
                        <AlertTriangle className="w-4 h-4 inline ml-1 text-red-500" />
                      )}
                      {!isExpired(vehicle.insurance_expiry_date) && isExpiringSoon(vehicle.insurance_expiry_date) && (
                        <AlertTriangle className="w-4 h-4 inline ml-1 text-yellow-500" />
                      )}
                    </span>
                  </div> */}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between pt-4 border-t">
                  {/* Driver Assignment Button */}
                  <button
                    onClick={() => {
                      setSelectedVehicle(vehicle);
                      setShowAssignmentModal(true);
                    }}
                    className={`flex items-center gap-1 p-2 rounded-lg transition-colors ${
                      assignedDriver 
                        ? 'text-orange-600 hover:bg-orange-50' 
                        : 'text-green-600 hover:bg-green-50'
                    }`}
                    title={assignedDriver ? 'Reassign driver' : 'Assign driver'}
                  >
                    <UserPlus className="w-4 h-4" />
                  </button>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onEditVehicle(vehicle)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit vehicle"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteVehicle(vehicle.id, vehicle.registration_no)}
                      disabled={deleteLoading === vehicle.id}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      title="Delete vehicle"
                    >
                      {deleteLoading === vehicle.id ? (
                        <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Assignment Modal */}
      {showAssignmentModal && selectedVehicle && (
        <AssignmentModal 
          vehicle={selectedVehicle}
          onClose={() => {
            setShowAssignmentModal(false);
            setSelectedVehicle(null);
          }} 
        />
      )}
    </div>
  );
};

export default VehiclesList;